// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { GripVertical, X } from 'lucide-react'
import type { Rect, LayerItemData } from './types'
import styles from './LayerControlsOverlay.module.css'

interface LayerControlsOverlayProps {
    frameId: string
    frameRect: Rect
    layers: LayerItemData[]
    contentScale: number
    activeLayerId?: string
    onSelectLayer?: (frameId: string, layerId: string) => void
    onReorderLayers: (frameId: string, fromIndex: number, toIndex: number) => void
    onRemoveLayer: (frameId: string, layerId: string) => void
}

interface DragState {
    draggedIndex: number | null
    dropTargetIndex: number | null
    dragStartY: number
    currentY: number
    ghostX: number
    ghostY: number
}

export function LayerControlsOverlay(props: LayerControlsOverlayProps): ReactElement {
    const {
        frameId,
        frameRect,
        layers,
        contentScale,
        activeLayerId,
        onSelectLayer,
        onReorderLayers,
        onRemoveLayer,
    } = props
    
    const [dragState, setDragState] = useState<DragState>({
        draggedIndex: null,
        dropTargetIndex: null,
        dragStartY: 0,
        currentY: 0,
        ghostX: 0,
        ghostY: 0,
    })
    
    const dragStateRef = useRef(dragState)
    const itemRefs = useRef<(HTMLDivElement | null)[]>([])
    
    useEffect(() => {
        dragStateRef.current = dragState
    }, [dragState])
    
    const sortedLayers = [...layers].sort((a, b) => b.zIndex - a.zIndex)
    
    const scale = 1 / (contentScale || 1)
    const panelLeft = frameRect.x - 140
    const panelTop = frameRect.y
    
    useEffect(() => {
        const handleDocumentPointerMove = (e: PointerEvent): void => {
            const state = dragStateRef.current
            if (state.draggedIndex === null) return
            
            const deltaY = e.clientY - state.dragStartY
            const itemHeight = itemRefs.current[0]?.offsetHeight || 60
            const itemsToMove = Math.round(deltaY / (itemHeight + 4))
            const newIndex = Math.max(0, Math.min(sortedLayers.length - 1, state.draggedIndex + itemsToMove))
            
            setDragState(prev => ({
                ...prev,
                currentY: e.clientY,
                ghostX: e.clientX,
                ghostY: e.clientY,
                dropTargetIndex: newIndex !== prev.draggedIndex ? newIndex : null,
            }))
        }
        
        const handleDocumentPointerUp = (): void => {
            const state = dragStateRef.current
            if (state.draggedIndex === null) return
            
            if (state.dropTargetIndex !== null && state.dropTargetIndex !== state.draggedIndex) {
                onReorderLayers(frameId, state.draggedIndex, state.dropTargetIndex)
            }
            
            setDragState({
                draggedIndex: null,
                dropTargetIndex: null,
                dragStartY: 0,
                currentY: 0,
                ghostX: 0,
                ghostY: 0,
            })
        }
        
        if (dragState.draggedIndex !== null) {
            document.addEventListener('pointermove', handleDocumentPointerMove)
            document.addEventListener('pointerup', handleDocumentPointerUp)
            
            return () => {
                document.removeEventListener('pointermove', handleDocumentPointerMove)
                document.removeEventListener('pointerup', handleDocumentPointerUp)
            }
        }
    }, [dragState.draggedIndex, frameId, onReorderLayers, sortedLayers.length])
    
    const handleDragPointerDown = (e: React.PointerEvent, index: number): void => {
        if (e.button !== 0) return
        
        e.preventDefault()
        e.stopPropagation()
        
        setDragState({
            draggedIndex: index,
            dropTargetIndex: null,
            dragStartY: e.clientY,
            currentY: e.clientY,
            ghostX: e.clientX,
            ghostY: e.clientY,
        })
    }
    
    const handleRemoveLayer = (layerId: string): void => {
        if (sortedLayers.length <= 1) return
        onRemoveLayer(frameId, layerId)
    }
    
    if (sortedLayers.length <= 1) return <></>
    
    const draggedLayer = dragState.draggedIndex !== null ? sortedLayers[dragState.draggedIndex] : null
    
    return (
        <>
            <div
                className={styles.layerControls}
                style={{
                    left: panelLeft,
                    top: panelTop,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top right',
                }}
                data-canvas-ui="true"
            >
                <div className={styles.layerControlsTitle}>Layers</div>

                {sortedLayers.map((layer, index) => {
                    const isDragging = dragState.draggedIndex === index
                    const isDropTarget = dragState.dropTargetIndex === index
                    const isActive = !!activeLayerId && layer.id === activeLayerId
                    
                    return (
                        <div
                            key={layer.id}
                            ref={el => { itemRefs.current[index] = el }}
                            className={`${styles.layerItem} ${isActive ? styles.activeLayer : ''} ${isDragging ? styles.dragging : ''} ${isDropTarget ? styles.dropTarget : ''}`}
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                onSelectLayer?.(frameId, layer.id)
                            }}
                        >
                            <div className={styles.dragHandle}>
                                <GripVertical
                                    className={styles.dragIcon}
                                    onPointerDown={(e) => handleDragPointerDown(e, index)}
                                />
                            </div>
                            
                            <img
                                src={layer.src}
                                alt=""
                                className={styles.thumbnail}
                                draggable={false}
                            />
                            
                            <button
                                className={styles.deleteButton}
                                disabled={sortedLayers.length <= 1}
                                style={sortedLayers.length <= 1 ? { opacity: 0.3, cursor: 'not-allowed' } : undefined}
                                onPointerDown={(e) => {
                                    e.stopPropagation()
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleRemoveLayer(layer.id)
                                }}
                                title="Remove layer"
                            >
                                <X className={styles.deleteIcon} />
                            </button>
                        </div>
                    )
                })}
            </div>
            
            {/* Drag ghost - render to body via portal to avoid canvas transforms */}
            {draggedLayer && dragState.draggedIndex !== null && createPortal(
                <div
                    className={styles.dragGhost}
                    style={{
                        left: `${dragState.ghostX}px`,
                        top: `${dragState.ghostY}px`,
                        transform: 'translate(-50%, -50%)',
                    }}
                    data-canvas-ui="true"
                >
                    <div className={styles.dragHandle}>
                        <GripVertical className={styles.dragIcon} />
                    </div>
                    
                    <img
                        src={draggedLayer.src}
                        alt=""
                        className={styles.thumbnail}
                        draggable={false}
                    />
                    
                    <button
                        className={styles.deleteButton}
                        style={{ pointerEvents: 'none' }}
                        title="Remove layer"
                    >
                        <X className={styles.deleteIcon} />
                    </button>
                </div>,
                document.body
            )}
        </>
    )
}
